import { Box, Button, CircularProgress, Link } from "@mui/material";
import AppIcon from "ui/components/icons/AppIcon";
import { iconDownload, iconArrowBackIos } from "ui/components/icons";
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterPanel,
  GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import {
  CommentContent,
  Filter,
  GlobalSearchSelectOption,
} from "data/@types/GlobalSearchData";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { EmptyTabAlert } from "ui/partials/details/EmptyTabAlert";
import { DateFormat, formatDateTimeInUserTimeZone } from "utils/date";
import { downloadCommentsPdf } from "utils/pdf/commentPdf";
import getEntityDetailsRoute from "utils/route/getEntityDetailsRoute";
import { GlobalSearchTabs } from "../../partials/global-search/GlobalSearchTabs";

type GlobalSearchAdvancedTableProps<T, P> = {
  onBack: () => void;
  rows: T[];
  columns: GridColDef[];
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnVisibilityChange: (columnName: string, isVisible: boolean) => void;
  filters: Filter[];
  applyFilters: (rows: T[]) => P[];
  activityType: GlobalSearchSelectOption;
  getRowId: (row: P) => string;
  emptyTestId: string;
  hideFooter?: boolean;
  hideFooterPagination?: boolean;
  isLoading?: boolean;
  fetchError?: boolean;
};

export function getSharedEntityColumns(handleClose: () => void): GridColDef[] {
  return [
    {
      field: "entityName",
      headerName: "Entity Name",
      minWidth: 250,
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Link
          to={getEntityDetailsRoute(
            row.entityType,
            row.entityId,
            row.entityId,
            row.entityType,
          )}
          onClick={() => handleClose()}
          component={RouterLink}
          sx={{
            textDecoration: "none",
            color: "info.main",
            fontWeight: "bold",
          }}
        >
          {row?.entityName}
        </Link>
      ),
    },
    { field: "entityType", headerName: "Entity Type" },
    { field: "universe", headerName: "Universe" },
  ];
}

declare module "@mui/x-data-grid" {
  interface NoResultsOverlayPropsOverrides {
    testId: string;
  }
  interface NoRowsOverlayPropsOverrides {
    testId: string;
    fetchError: boolean;
  }
}

const NoResultsOverlay = ({ testId }: { testId?: string }) => (
  <EmptyTabAlert testId={testId ?? ""} />
);

const NoRowsOverlay = ({
  testId,
  fetchError,
}: {
  testId?: string;
  fetchError?: boolean;
}) =>
  fetchError ? (
    <EmptyTabAlert testId={`${testId ?? ""}-error`}>
      Failed to load results. Please try again.
    </EmptyTabAlert>
  ) : (
    <EmptyTabAlert testId={testId ?? ""} />
  );

export const GlobalSearchAdvancedTable = <T, P>({
  onBack,
  rows,
  columns,
  columnVisibilityModel,
  onColumnVisibilityChange,
  filters,
  applyFilters,
  activityType,
  getRowId,
  emptyTestId,
  hideFooter = false,
  hideFooterPagination = false,
  isLoading = false,
  fetchError = false,
}: GlobalSearchAdvancedTableProps<T, P>) => {
  const apiRef = useGridApiRef();
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [pdfProgress, setPdfProgress] = React.useState("");

  const handlePrintComments = async () => {
    const filteredIds: number[] = (applyFilters(rows) as CommentContent[]).map(
      (r) => r.id,
    );
    const uniqueFilteredIds = Array.from(new Set(filteredIds));

    try {
      setPdfLoading(true);
      setPdfProgress("Preparing...");

      await downloadCommentsPdf(uniqueFilteredIds, (progress) => {
        setPdfProgress(progress);
      });

      setPdfProgress("PDF downloaded!");
      setTimeout(() => {
        setPdfLoading(false);
        setPdfProgress("");
      }, 2000);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      setPdfProgress("Error generating PDF");
      setTimeout(() => {
        setPdfLoading(false);
        setPdfProgress("");
      }, 3000);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 350px)" }}>
      <Box sx={{ width: "80%", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button
            size="small"
            onClick={onBack}
            variant="text"
            sx={{ lineHeight: 0, my: 1 }}
            startIcon={<AppIcon icon={iconArrowBackIos} fontSize="small" />}
          >
            Back to the list view
          </Button>
          <Box sx={{ display: "flex", gap: 2 }}>
            {activityType === "Comments" && (
              <Button
                size="small"
                variant="text"
                onClick={handlePrintComments}
                disabled={pdfLoading}
                sx={{ lineHeight: 0, my: 1 }}
                startIcon={
                  pdfLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AppIcon icon={iconDownload} fontSize="small" />
                  )
                }
              >
                {pdfLoading ? pdfProgress : "Export to PDF"}
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              onClick={() =>
                apiRef.current.exportDataAsCsv({
                  fileName: `${activityType.toLowerCase()}-data-${formatDateTimeInUserTimeZone(new Date(), { customFormat: DateFormat.DATE })}`,
                  utf8WithBom: true,
                })
              }
              sx={{ lineHeight: 0, my: 1 }}
              startIcon={<AppIcon icon={iconDownload} fontSize="small" />}
            >
              Export to CSV
            </Button>
          </Box>
        </Box>
        <DataGrid
          apiRef={apiRef}
          rows={applyFilters(rows)}
          columns={columns}
          getRowId={getRowId}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          columnVisibilityModel={columnVisibilityModel}
          hideFooter={hideFooter}
          hideFooterPagination={hideFooterPagination}
          hideFooterSelectedRowCount
          loading={isLoading}
          slots={{
            filterPanel: GridFilterPanel,
            noResultsOverlay: NoResultsOverlay,
            noRowsOverlay: NoRowsOverlay,
          }}
          slotProps={{
            noResultsOverlay: { testId: emptyTestId },
            noRowsOverlay: { testId: emptyTestId, fetchError },
          }}
          initialState={{ columns: { columnVisibilityModel } }}
          sx={{
            border: 0,
            color: "text.secondary",
            ".MuiDataGrid-scrollbarFiller--header": { background: "#222" },
            ".MuiDataGrid-filler": { background: "#222" },
            ".MuiDataGrid-columnHeader": { background: "#222" },
          }}
        />
      </Box>
      <GlobalSearchTabs
        columns={columns}
        columnsVisibility={columnVisibilityModel}
        onColumnVisibilityChange={onColumnVisibilityChange}
        filters={filters}
        activityType={activityType}
      />
    </Box>
  );
};
